import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";

/**
 * Owner-only edit + delete affordance for a single listing.
 *
 * Visibility rules:
 * - Renders nothing unless the viewer is the seller (or admin).
 * - Edit dialog covers the 4 most-frequently-corrected fields:
 *   title, price, description, shipping_estimate.
 * - Delete confirms with a soft-cancel for auctions that already have
 *   bids (server enforces the same logic — UI just mirrors the copy).
 */
export default function OwnerListingActions({ listing, onChange, onDeleted }) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState(listing.title);
  const [price, setPrice] = useState(String(listing.price ?? ""));
  const [description, setDescription] = useState(listing.description || "");
  const [shipping, setShipping] = useState(
    listing.shipping_estimate != null ? String(listing.shipping_estimate) : ""
  );

  const isAuctionWithBids =
    listing.type === "auction" && Number(listing.bid_count || 0) > 0;
  const isSold = listing.status === "sold";

  const save = async () => {
    const body = {};
    if (title !== listing.title) body.title = title.trim();
    if (description !== listing.description) body.description = description;
    const numericPrice = parseFloat(price);
    if (!isNaN(numericPrice) && numericPrice !== Number(listing.price)) {
      if (numericPrice <= 0) {
        toast.error("Price must be greater than 0");
        return;
      }
      if (numericPrice > 999999.99) {
        toast.error("Price cannot exceed $999,999.99");
        return;
      }
      body.price = numericPrice;
    }
    const numericShip = parseFloat(shipping);
    if (shipping === "") {
      if (listing.shipping_estimate != null) body.shipping_estimate = null;
    } else if (!isNaN(numericShip) && numericShip !== Number(listing.shipping_estimate)) {
      body.shipping_estimate = numericShip;
    }
    if (Object.keys(body).length === 0) {
      setEditOpen(false);
      return;
    }
    setBusy(true);
    try {
      const res = await api.patch(`/listings/${listing.id}`, body);
      toast.success("Listing updated");
      setEditOpen(false);
      onChange?.(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/listings/${listing.id}`);
      if (res.data.soft_deleted) {
        toast.success("Auction cancelled. Bidders have been notified.");
      } else {
        toast.success("Listing deleted");
      }
      setDeleteOpen(false);
      if (onDeleted) {
        onDeleted();
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="owner-listing-actions">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setEditOpen(true)}
        className="text-zinc-700 border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]"
        data-testid="listing-edit-btn"
        disabled={isSold}
      >
        <PencilSimpleIcon size={14} className="mr-1" /> Edit
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setDeleteOpen(true)}
        className="text-red-600 border-red-200 hover:border-red-400 hover:bg-red-50"
        data-testid="listing-delete-btn"
        disabled={isSold}
      >
        <TrashIcon size={14} className="mr-1" />
        {isAuctionWithBids ? "Cancel" : "Delete"}
      </Button>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="listing-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit listing</DialogTitle>
            <DialogDescription>
              {isAuctionWithBids
                ? "This auction has live bids — title, description, photos, and shipping can be edited but price is locked."
                : "Make changes to your listing. Saves immediately."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                Title
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                className="mt-1"
                data-testid="listing-edit-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                  Price (USD)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="999999.99"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="mt-1"
                  data-testid="listing-edit-price"
                  disabled={isAuctionWithBids}
                />
                {isAuctionWithBids && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Locked — auction has bids.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                  Flat shipping
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  placeholder="Free shipping"
                  className="mt-1"
                  data-testid="listing-edit-shipping"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                Description
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                maxLength={2000}
                className="mt-1"
                data-testid="listing-edit-description"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              disabled={busy}
              data-testid="listing-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              className="bg-[#1D78AB] hover:bg-[#176089] text-white"
              data-testid="listing-edit-save"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="listing-delete-dialog">
          <DialogHeader>
            <DialogTitle>
              {isAuctionWithBids ? "Cancel this auction?" : "Delete this listing?"}
            </DialogTitle>
            <DialogDescription>
              {isAuctionWithBids ? (
                <>
                  This auction has <b>{listing.bid_count}</b> bid
                  {listing.bid_count === 1 ? "" : "s"}. Cancelling marks the
                  listing <b>cancelled</b> and notifies the high bidder. Bid
                  history is preserved so buyers can still see what happened.
                </>
              ) : (
                <>
                  This permanently removes the listing. Bookmarks pointing at
                  it will be cleared. This can't be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={busy}
              data-testid="listing-delete-cancel"
            >
              Keep it
            </Button>
            <Button
              onClick={remove}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="listing-delete-confirm"
            >
              {busy
                ? "Working…"
                : isAuctionWithBids
                ? "Cancel auction"
                : "Delete listing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
